import { httpsCallable } from 'firebase/functions';

import { FormVersion, Form, DocId } from '#types/System.types';
import type { ArTableId, ArCell, TypeId } from '#types/System.Artifacts.types';
import { FormStateId } from '#types/System.Parameters.types';
import { functions } from './firebaseFunctions';


/**
 * Request object for creating a new form version.
 */
interface CreateFormVersionRequest {
    formId: string;
    sourceVersionId?: string;
    newState: FormStateId;
    setVersionData?: Record<string, any>;
}

/**
 * Creates a new form version using Firebase Cloud Functions.
 * @param {string} formId - The ID of the form.
 * @param {string | undefined} sourceVersionId - The version to branch from; pass `undefined` to build the first version from the form type alone (its initial state and default layout), which is how a form with zero versions gets one back. `newState` is then ignored by the callable.
 * @param {FormStateId} newState - The state for the new version.
 * @param {Record<string, any>} setVersionData - Optional additional field data to set.
 * @returns {Promise<string>} A promise that resolves to the ID of the created form version.
 * @throws {Error} If the form version creation fails.
 */
export const createFormVersion = async (
    formId: string,
    sourceVersionId: string | undefined,
    newState: FormStateId,
    setVersionData?: Record<string, any>
): Promise<string> => {
    const callCreateFormVersion = httpsCallable<CreateFormVersionRequest, string>(functions, 'createFormVersion');

    /**
     * Spread, not spelled: the callable client encodes a property whose value is
     * `undefined` as `null` and drops only an absent key, so spelling
     * `sourceVersionId` sent `null` for a first version and the server's
     * destructuring default never fired (SYD-2445).
     */
    const requestData: CreateFormVersionRequest = {
        formId,
        newState,
        ...(sourceVersionId ? { sourceVersionId } : {}),
        ...(setVersionData ? { setVersionData } : {})
    };

    try {
        const result = await callCreateFormVersion(requestData);
        console.log('New form version created:', result.data);
        return result.data;
    } catch (error) {
        console.error('Error creating form version:', error);
        throw new Error(`Failed to create form version: ${error}`);
    }
};


/**
 * Request object for creating a new form.
 */
export type InitialTableRows = {
    tableId: ArTableId;
    rows: Array<{ cells: Record<TypeId, ArCell> }>;
};

export interface CreateFormRequest {
    formType: string;
    title?: string;
    organization?: string;
    setFormData?: Partial<Form>;
    setVersionData?: Partial<FormVersion>;
    initialTableRows?: InitialTableRows;
}

/**
 * Creates a new form using Firebase Cloud Functions.
 * @param {CreateFormRequest} requestData - The data required to create a new form.
 * @returns {Promise<string>} A promise that resolves to the ID of the created form.
 * @throws {Error} If the form creation fails.
 */
export const createForm = async (requestData: CreateFormRequest): Promise<string> => {
    const callCreateForm = httpsCallable<CreateFormRequest, string>(functions, 'createForm');

    try {
        const result = await callCreateForm(requestData);
        return result.data;
    } catch (error) {
        const errorMessage = `Failed to create form: ${error}`;
        console.error(`Error calling createForm function:`, error);
        throw new Error(errorMessage);
    }
};

/**
 * Request object for creating a new form from an existing form.
 */
interface CreateFormFromFormRequest {
    formId: string;
    sourceVersionId: string;
    newFormType: string;
    title?: string;
    organization?: string;
    setFormData?: Partial<Form>;
    setVersionData?: Partial<FormVersion>;
}

export interface CreateFormFromFormResponse {
    newFormId: string;
    newVersionId: string;
}

export interface BatchCreateFormFromFormResponse {
    results: Array<{
        success: boolean;
        newFormId?: string;
        newVersionId?: string;
        sourceFormId: string;
        error?: string;
    }>;
    successCount: number;
    failureCount: number;
}

export type CreateFormFromFormInput = CreateFormFromFormRequest | CreateFormFromFormRequest[];
export type CreateFormFromFormOutput = CreateFormFromFormResponse | BatchCreateFormFromFormResponse;

/**
 * Creates a new form from an existing form using Firebase Cloud Functions.
 * Supports both single and batch operations - pass a single request object or an array of requests.
 * @param {CreateFormFromFormInput} requestData - Single request or array of requests.
 * @returns {Promise<CreateFormFromFormOutput>} Single response or batch response.
 * @throws {Error} If the form creation fails.
 */
export const createFormFromForm = async (requestData: CreateFormFromFormInput): Promise<CreateFormFromFormOutput> => {
    if (!functions) {
        throw new Error('Firebase Functions not initialized');
    }

    const callCreateFormFromForm = httpsCallable<CreateFormFromFormInput, CreateFormFromFormOutput>(
        functions,
        'createFormFromForm'
    );

    try {
        const result = await callCreateFormFromForm(requestData);
        return result.data;
    } catch (error) {
        const errorMessage = `Failed to create form from existing form: ${error}`;
        console.error(`Error calling createFormFromForm function:`, error);
        throw new Error(errorMessage);
    }
};

/**
 * Request object for setting a form version state.
 */
interface SetFormStateRequest {
    formId: DocId;
    formVersionId: DocId;
    newState: FormStateId;
}

/**
 * Sets the state of a form version using Firebase Cloud Functions.
 * @param {DocId} formId - The ID of the form.
 * @param {DocId} versionId - The ID of the form version.
 * @param {FormStateId} newState - The new state to set.
 * @returns {Promise<void>} A promise that resolves when the state is set.
 * @throws {Error} If setting the form state fails.
 */
export const setFormState = async (
    formId: DocId,
    versionId: DocId,
    newState: FormStateId
): Promise<void> => {
    const callSetFormState = httpsCallable<SetFormStateRequest, void>(functions, 'setFormState');

    const requestData: SetFormStateRequest = {
        formId,
        formVersionId: versionId,
        newState
    };

    try {
        await callSetFormState(requestData);
    } catch (error) {
        console.error('Error setting form state:', error);
        throw new Error(`Failed to set form state: ${error}`);
    }
};

/**
 * Request object for setting a form version as current.
 */
interface SetCurrentFormVersionRequest {
    formId: DocId;
    versionId: DocId;
}

/**
 * Sets a form version as the current version using Firebase Cloud Functions.
 * @param {DocId} formId - The ID of the form.
 * @param {DocId} versionId - The ID of the form version to set as current.
 * @returns {Promise<void>} A promise that resolves when the version is set as current.
 * @throws {Error} If setting the current version fails.
 */
export const setAsCurrentVersion = async (
    formId: DocId,
    versionId: DocId
): Promise<void> => {
    const callSetCurrentVersion = httpsCallable<SetCurrentFormVersionRequest, void>(functions, 'setCurrentFormVersion');

    const requestData: SetCurrentFormVersionRequest = {
        formId,
        versionId
    };

    try {
        await callSetCurrentVersion(requestData);
    } catch (error) {
        console.error('Error setting current form version:', error);
        throw new Error(`Failed to set current form version: ${error}`);
    }
};

/**
 * Archive service for managing document archiving
 */

export interface ArchiveArtifactParams {
  artifactType: 'form' | 'entityRecord' | 'formType' | 'entity' | 'formVersion';
  artifactId: string;
  parentId?: string;
  action: 'archive' | 'unarchive';
  reason?: string;
}

/**
 * Archives or unarchives an artifact (form, entity, formType, entityRecord, or
 * formVersion). Permanent deletion of archived copies happens on a schedule, not
 * from the client.
 */
export const archiveArtifact = httpsCallable<ArchiveArtifactParams, any>(
  functions,
  'archiveArtifact'
);

/**
 * Duplicate forms service
 */

export interface DuplicateFormsRequest {
    formId: string;
    assignedTo?: string;
}

export interface DuplicateFormsResponse {
    success: boolean;
    newFormId?: string;
    newVersionId?: string;
    sourceFormId: string;
    error?: string;
}

export interface BatchDuplicateFormsResponse {
    results: Array<DuplicateFormsResponse>;
    successCount: number;
    failureCount: number;
}

export type DuplicateFormsInput = DuplicateFormsRequest | DuplicateFormsRequest[];
export type DuplicateFormsOutput = DuplicateFormsResponse | BatchDuplicateFormsResponse;

/**
 * Duplicates forms (creates exact copies with same form type).
 * Supports both single and batch operations - pass a single request object or an array of requests.
 * @param {DuplicateFormsInput} requestData - Single request or array of requests.
 * @returns {Promise<DuplicateFormsOutput>} Single response or batch response.
 * @throws {Error} If the duplication fails.
 */
export const duplicateForms = async (requestData: DuplicateFormsInput): Promise<DuplicateFormsOutput> => {
    if (!functions) {
        throw new Error('Firebase Functions not initialized');
    }

    const callDuplicateForms = httpsCallable<DuplicateFormsInput, DuplicateFormsOutput>(
        functions,
        'duplicateForms'
    );

    try {
        const result = await callDuplicateForms(requestData);
        return result.data;
    } catch (error) {
        const errorMessage = `Failed to duplicate forms: ${error}`;
        console.error(`Error calling duplicateForms function:`, error);
        throw new Error(errorMessage);
    }
};